import { FC } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop } from '@fortawesome/pro-solid-svg-icons';
import { useTranslations } from 'next-intl';
import { formatDuration } from './audioLevels';
import { Waveform } from './Waveform';

interface Props {
  elapsed: number;
  levels: number[];
  onStop: () => void;
}

// The recording in progress: a pulsing dot, the time so far, the live
// waveform and a Stop button.
export const RecordingBar: FC<Props> = ({ elapsed, levels, onStop }) => {
  const t = useTranslations();

  return (
    <div className={styles.bar} role="status">
      <span className={styles.dot} aria-hidden="true" />
      <span className="sr-only">{t('memoComposer.audio.recording')}</span>
      <span className={styles.time}>{formatDuration(elapsed)}</span>
      <Waveform levels={levels} tone="recording" />
      <button type="button" onClick={onStop} className={styles.stop}>
        <FontAwesomeIcon icon={faStop} className="text-[11px]" aria-hidden />
        <span>{t('memoComposer.audio.stop')}</span>
      </button>
    </div>
  );
};

const styles = {
  // Translucent red, so the bar tints the card in light and dark alike.
  bar: 'flex items-center gap-3 rounded-xl border border-mf-red-600/20 bg-mf-red-600/8 px-3 py-2',
  dot: 'size-2.5 shrink-0 animate-pulse rounded-full bg-mf-red-600',
  time: 'w-10 shrink-0 text-sm font-semibold tabular-nums text-ink-primary',
  stop: classNames(
    'inline-flex shrink-0 items-center gap-2 rounded-full bg-ink-primary px-3.5 py-1.5 text-sm font-semibold text-surface-card',
    'hover:opacity-90 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
};
